import React from 'react';
import { OccupancyRule, OccupancyOperator, RateAction } from '../../types';
import Select from 'components/UI/V3/Forms/Select';
import { Input } from 'components/UI/V3/Forms/Input/Input';
import PriceField from 'components/UI/V3/Forms/PriceField';
import { NumberField } from 'components/UI/V3/Forms/NumberField';
import { faTimes } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import styles from './OccupancyRuleRow.module.scss';

interface OccupancyRuleRowProps {
    rule: OccupancyRule;
    showDelete: boolean;
    takenOperators: OccupancyOperator[];
    onChange: (rule: OccupancyRule) => void;
    onRemove: (ruleId: string) => void;
}

const allOperatorOptions = [
    { value: 'greater_than', label: 'Greater than' },
    { value: 'greater_than_or_equal', label: 'Greater/equal' },
    { value: 'less_than', label: 'Less than' },
    { value: 'less_than_or_equal', label: 'Less/equal' }
];

const actionOptions = [
    { value: 'increase', label: 'Increase' },
    { value: 'decrease', label: 'Decrease' }
];

const currencyOptions = [
    { value: 'USD', label: 'USD' },
    { value: 'PERCENT', label: '%' }
];

export const OccupancyRuleRow: React.FC<OccupancyRuleRowProps> = ({
    rule,
    showDelete,
    takenOperators,
    onChange,
    onRemove
}) => {
    const operatorOptions = allOperatorOptions.filter(
        opt => opt.value === rule.operator || !takenOperators.includes(opt.value as OccupancyOperator)
    );
    const handleOperatorChange = (value: string) => {
        onChange({ ...rule, operator: value as OccupancyOperator });
    };

    const handlePercentageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = Math.max(0, Math.min(100, Number(e.target.value) || 0));
        onChange({ ...rule, percentage: value });
    };

    const handleActionChange = (value: string) => {
        onChange({ ...rule, action: value as RateAction });
    };

    const handleAmountChange = (value: number | undefined) => {
        onChange({ ...rule, amount: value || 0 });
    };

    const handleCurrencyChange = (value: string) => {
        onChange({ ...rule, currency: value });
    };

    return (
        <div className={styles.wrapper}>
            <div className={styles.container}>
                <div className={styles.ruleCard}>
                    <div className={styles.ruleContent}>
                        <span className={styles.label}>When occupancy is</span>

                        <Select
                            value={rule.operator}
                            options={operatorOptions}
                            onChange={handleOperatorChange}
                            className={styles.operatorSelect}
                        />

                        <div className={styles.percentageInputWrapper}>
                            <Input
                                type="number"
                                value={rule.percentage.toString()}
                                onChange={handlePercentageChange}
                                className={styles.percentageInput}
                                autoFocus={false}
                                min={0}
                                max={100}
                            />
                            <span className={styles.percentageSymbol}>%</span>
                        </div>

                        <Select
                            value={rule.action}
                            options={actionOptions}
                            onChange={handleActionChange}
                            className={styles.actionSelect}
                        />

                        <span className={styles.label}>rate by</span>

                        <div className={styles.amountWrapper}>
                            <div className={styles.amountInputContainer}>
                                {rule.currency === 'PERCENT' ? (
                                    <NumberField
                                        scale={2}
                                        value={rule.amount > 0 ? rule.amount : undefined}
                                        onChange={handleAmountChange}
                                        placeholder="0.00"
                                        className={styles.amountInput}
                                    />
                                ) : (
                                    <PriceField
                                        scale={2}
                                        value={rule.amount > 0 ? rule.amount : undefined}
                                        onChange={handleAmountChange}
                                        placeholder="0.00"
                                        className={styles.amountInput}
                                    />
                                )}
                            </div>

                            <Select
                                value={rule.currency}
                                options={currencyOptions}
                                onChange={handleCurrencyChange}
                                className={styles.currencySelect}
                            />
                        </div>
                    </div>
                </div>
            </div>
            {showDelete && (
                <button
                    type="button"
                    className={styles.deleteButton}
                    onClick={() => onRemove(rule.id)}
                    title="Remove rule"
                >
                    <FontAwesomeIcon icon={faTimes} />
                </button>
            )}
        </div>
    );
};
